// OAS Stream Mapping — the OAS-side sibling of the Refinery Product Mapping. Tags each stream
// (grade) with the instrument it's measured by, which implies an A/B/C/D accuracy grade (overridable).
// Persisted shared/site-wide via the same endpoint as the product mapping (?type=stream).
// See MASS_BALANCE_ENHANCEMENT_PLAN.md (Phase 2+ §4).

const API = process.env.REACT_APP_API_ENDPOINT || '';

export interface StreamTag { instrument: string; grade: string; }
export interface StreamMapping { version: number; assignments: Record<string, StreamTag>; updatedAt?: string; }

export interface InstrumentDef { id: string; label: string; grade: string; }
// Instrument groupings -> default accuracy grade (tunable). Best-measured first.
export const INSTRUMENTS: InstrumentDef[] = [
  { id: 'coriolis', label: 'Coriolis meter', grade: 'A' },
  { id: 'pd_turbine', label: 'PD / Turbine meter', grade: 'B' },
  { id: 'pv', label: 'PV meter', grade: 'B' },
  { id: 'measured', label: 'Measured (other metered)', grade: 'B' },
  { id: 'calculated', label: 'Calculated', grade: 'C' },
  { id: 'estimated', label: 'Estimated', grade: 'D' },
];
export const GRADES = ['A', 'B', 'C', 'D'];
export const GRADE_ORDER: Record<string, number> = { A: 0, B: 1, C: 2, D: 3 };

export const instrumentLabel = (id?: string): string => INSTRUMENTS.find((i) => i.id === id)?.label || '';
export const instrumentGrade = (id?: string): string | undefined => INSTRUMENTS.find((i) => i.id === id)?.grade;

export const defaultStreamMapping = (): StreamMapping => ({ version: 1, assignments: {} });

// The effective A/B/C/D for a stream: explicit override, else the instrument's default, else undefined.
export const gradeForStream = (m: StreamMapping | null | undefined, key: string): string | undefined => {
  const t = m?.assignments?.[key];
  if (!t) return undefined;
  return t.grade || instrumentGrade(t.instrument);
};

const gradeColorMap: Record<string, string> = { A: '#2e7d32', B: '#1976d2', C: '#ed6c02', D: '#d32f2f' };
export const gradeColor = (g?: string): string => (g ? gradeColorMap[g] || '#757575' : '#bdbdbd');

// Per-dataset stream mapping (each customer's streams differ) — kept fully separate; one dataset
// never inherits another's tags. Null => caller uses defaultStreamMapping() (empty).
export async function loadStreamMapping(dataset?: string, idToken?: string): Promise<StreamMapping | null> {
  const ds = dataset ? `&dataset=${encodeURIComponent(dataset)}` : '';
  try {
    const res = await fetch(`${API}/mass-balance-mapping?type=stream${ds}`, {
      headers: idToken ? { Authorization: `Bearer ${idToken}` } : {},
    });
    if (res.ok) { const data = await res.json(); if (data?.mapping) return data.mapping; }
  } catch { /* fall through */ }
  return null;
}

export async function saveStreamMapping(m: StreamMapping, dataset?: string, idToken?: string): Promise<{ backend: boolean }> {
  const ds = dataset ? `&dataset=${encodeURIComponent(dataset)}` : '';
  try {
    const res = await fetch(`${API}/mass-balance-mapping?type=stream${ds}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
      },
      body: JSON.stringify({ mapping: { ...m, updatedAt: new Date().toISOString() } }),
    });
    return { backend: res.ok };
  } catch {
    return { backend: false };
  }
}
